import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Leaf, Heart, Rocket, ArrowRight, Check } from 'lucide-react';

const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Shield,
      title: 'Technology-Enabled Solutions',
      description: 'Enterprise-grade software solutions designed to automate business operations while driving efficiency and scalability.',
      color: 'from-[#B8336A] to-[#B8336A]/70'
    },
    {
      icon: Leaf,
      title: 'Sustainable IT Practices',
      description: 'Implementing resilient, scalable IT frameworks that balance innovation with sustainability and business continuity.',
      color: 'from-[#2D6A6A] to-[#2D6A6A]/70'
    },
    {
      icon: Heart,
      title: 'Dedicated Client Success',
      description: 'A client-first approach with tailored solutions, continuous support, and measurable outcomes for every engagement.',
      color: 'from-[#3B82F6] to-[#3B82F6]/70'
    },
    {
      icon: Rocket,
      title: 'Committed to Your Success',
      description: 'We prioritize innovation and personalized support, working closely with you to drive seamless digital transformation.',
      color: 'from-[#B8336A] to-[#2D6A6A]'
    }
  ];

  const benefits = [
    'End-to-end IT solutions under one roof',
    'Certified and experienced professionals',
    'Agile development methodology',
    '24/7 support and maintenance',
    'Scalable and custom solutions',
    'Competitive pricing models'
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
        <div className="absolute inset-0 bg-gradient-to-l from-[#B8336A] to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 text-sm font-medium text-[#B8336A] mb-4">
            Why Choose Us?
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Building a Smarter Future with
            <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> IT Solutions</span>
          </h2>
          <p className="text-gray-600 text-lg">
            In today's digital-first world, secure and scalable IT solutions are essential. We combine expertise, innovation, and dedication to help organizations achieve sustainable growth.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon size={28} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Benefits List */}
          <div className="bg-gradient-to-br from-[#B8336A]/5 to-[#2D6A6A]/5 rounded-3xl p-8 lg:p-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              What Sets Us Apart
            </h3>
            <ul className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={14} className="text-white" />
                  </div>
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-full font-semibold hover:shadow-lg hover:shadow-[#B8336A]/30 transition-all duration-300 hover:-translate-y-1"
            >
              Contact Us
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
